import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, TextField, DialogActions, DialogContent, Grid, DialogTitle, Dialog, CircularProgress, Autocomplete } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { omit } from 'lodash'
import { DatePicker } from '@mui/x-date-pickers'
import { endOfYear, startOfYear } from 'date-fns'
import { useFirestore } from '../../hooks/useFirestore'
import { ACCOUNT_COLLECTION, ACCOUNT_ENTRY_COLLECTION, USER_COLLECTION } from '../../_constants/globals'
import { serverTimestamp } from 'firebase/firestore'
import { TRANSFER_ERROR, TRANSFER_SUCCESS } from '../../store/types'
import useAuth from '../../hooks/useAuth'


export const ApproDialog = ({ open, onClose }) => {
  
  const accountHooks = useFirestore(ACCOUNT_COLLECTION)
  const accounts = accountHooks.getDocs()
  const selectedUser = useSelector(state => state.data.selectedUser)
  const selectedAccountId = useSelector(state => state.data.selectedAccountId)
  const dispatch = useDispatch()
  const entryHooks = useFirestore(ACCOUNT_ENTRY_COLLECTION)
  const userHooks = useFirestore(USER_COLLECTION)
  const profile = useAuth().getProfile()
  
  const [debit, setDebit] = useState(null)
  const [credit, setCredit] = useState(null)
  const [amount, setAmount] = useState(0)
  const [comment, setComment] = useState('Virement compte pilote')
  const [accountDate, setAccountDate] = useState(new Date())
  
  const valid = useMemo(() => credit && amount, [credit, amount])
  
  useEffect(() => {
    if (accounts) setDebit(accounts.find(el => el.name === 'Compte courant'))
  }, [accounts])
  
  useEffect(() => {
    if (accounts && selectedAccountId) setCredit(omit(accounts.find(el => el.id === selectedAccountId), ['ownerRef']))
    else if (accounts && selectedUser) setCredit(omit(accounts.find(el => el.id === selectedUser.accountRef.id), ['ownerRef']))
  }, [accounts, selectedUser, selectedAccountId])
  
  const handleSubmit = e => {
    e.preventDefault()
    console.info('create transfer', { debit, credit, amount, comment, accountDate })
    const batch = accountHooks.getBatch()
    batch.set(entryHooks.getDocRef(), {
      accountDate,
      accountRef: accountHooks.getDocRef(debit.id),
      comment,
      debit: amount,
      paymentType: 'transfer',
      _createdAtTime: serverTimestamp(),
      _createdByRef: userHooks.getDocRef(profile.id),
    })
    batch.set(entryHooks.getDocRef(), {
      accountDate,
      accountRef: accountHooks.getDocRef(credit.id),
      comment,
      credit: amount,
      paymentType: 'transfer',
      _createdAtTime: serverTimestamp(),
      _createdByRef: userHooks.getDocRef(profile.id),
    })
    return batch.commit()
      .then(() => dispatch({ type: TRANSFER_SUCCESS }))
      .then(onClose)
      .catch(err => dispatch({ type: TRANSFER_ERROR, payload: err }))
  }
  
  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle id='form-dialog-title'>Approvisionner un compte pilote</DialogTitle>
        <DialogContent>
          {!accounts
            ? <CircularProgress color='inherit' />
            : <Grid container spacing={3}>
              <Grid item container size={12} spacing={2} style={{ alignItems: 'center' }}>
                <Grid item size={6}>
                  <Autocomplete
                    id='debit-select'
                    options={accounts}
                    getOptionLabel={option => option.name}
                    renderInput={params =>
                      <TextField variant='standard' {...params} label='Compte à débiter' />
                    }
                    value={debit || null}
                    onChange={(event, newValue) => setDebit(newValue)}
                    isOptionEqualToValue={option => accounts.find(el => el.id === option.id)}
                  />
                </Grid>
                <Grid item size={4}>
                  <TextField
                    variant='standard'
                    margin='dense'
                    id='amount'
                    label='Montant'
                    type='number'
                    value={amount}
                    onChange={e => {
                      e.persist()
                      setAmount(parseFloat(e.target.value))
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item size={12}>
                <Autocomplete
                  id='credit-select'
                  options={accounts}
                  getOptionLabel={option => option.name}
                  renderInput={params =>
                    <TextField variant='standard' {...params} label='Compte à créditer' fullWidth />
                  }
                  value={credit}
                  onChange={(event, newValue) => setCredit(newValue)}
                  isOptionEqualToValue={option => accounts.find(el => el.id === option.id)}
                />
              </Grid>
              <Grid item size={12}>
                <DatePicker
                  disableToolbar
                  variant='inline'
                  inputFormat='dd-MM-yyyy'
                  margin='dense'
                  id='startDate-date'
                  label='Date du transfer'
                  value={accountDate}
                  onChange={setAccountDate}
                  minDate={startOfYear(new Date())}
                  maxDate={endOfYear(new Date())}
                />
              </Grid>
              <Grid item size={12}>
                <TextField
                  variant='standard'
                  margin='dense'
                  id='comment'
                  label='Commentaire'
                  fullWidth
                  value={comment || ''}
                  multiline
                  onChange={e => setComment(e.target.value)} />
              </Grid>
            </Grid>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} >Annuler</Button>
          <Button type='submit' color='primary' disabled={!valid}>Valider</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

ApproDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
}

export default ApproDialog
